<template>
    <v-container fluid class="assistant-dashboard">
            <div class="assistant-layout">
                <section class="assistant-column assistant-column--threads">
                    <v-sheet class="assistant-panel assistant-panel--threads" outlined>
                        <div class="assistant-panel__header">
                            <div>
                                <div class="assistant-kicker">Conversatii</div>
                                <h2 class="assistant-title">Asistent CRM</h2>
                            </div>
                            <v-btn icon small color="primary" :loading="threadsLoading" title="Conversatie noua" @click="startThread">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </div>

                        <v-list two-line dense class="assistant-thread-list pa-0">
                            <v-list-item
                                v-for="thread in threads"
                                :key="thread.id"
                                class="assistant-thread"
                                :class="{ 'assistant-thread--active': activeThread && activeThread.id === thread.id }"
                                @click="selectThread(thread)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>{{ thread.title || 'Conversatie noua' }}</v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ formatDateTime(thread.last_message_at) }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-chip x-small :color="thread.status === 'active' ? 'success' : 'grey'" dark>
                                        {{ thread.status === 'active' ? 'Activ' : 'Arhivat' }}
                                    </v-chip>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>

                        <div v-if="!threadsLoading && threads.length === 0" class="assistant-empty assistant-empty--panel">
                            <v-icon color="grey lighten-1" size="34">mdi-message-text-outline</v-icon>
                            <div>Nu exista conversatii.</div>
                        </div>
                    </v-sheet>
                </section>

                <section class="assistant-column assistant-column--chat">
                    <v-sheet class="assistant-panel assistant-panel--chat assistant-chat" outlined>
                        <div class="assistant-panel__header">
                            <div>
                                <div class="assistant-kicker">{{ workstationLabel }}</div>
                                <h1 class="assistant-title">Cautare conversationala</h1>
                            </div>
                            <v-btn
                                icon
                                small
                                color="grey darken-2"
                                :disabled="!activeThread"
                                title="Arhiveaza conversatia"
                                @click="archiveThread"
                            >
                                <v-icon>mdi-archive-outline</v-icon>
                            </v-btn>
                        </div>

                        <div class="assistant-suggestions">
                            <v-chip
                                v-for="suggestion in suggestions"
                                :key="suggestionKey(suggestion)"
                                small
                                outlined
                                class="assistant-suggestion"
                                @click="useSuggestion(suggestion)"
                            >
                                {{ suggestionLabel(suggestion) }}
                            </v-chip>
                        </div>

                        <div ref="messages" class="assistant-messages">
                            <div v-if="messages.length === 0 && !loading" class="assistant-chat-empty">
                                <v-icon color="primary" size="44">mdi-text-search</v-icon>
                                <h3>Conversatie noua</h3>
                                <p>{{ workstationLabel }}</p>
                            </div>
                            <div
                                v-for="message in messages"
                                :key="message.local_id || message.id"
                                :class="['assistant-message', `assistant-message--${message.role}`]"
                            >
                                <div class="assistant-message__content">
                                    <div
                                        class="assistant-message__body"
                                        v-html="formatMessage(message.content)"
                                    >
                                    </div>
                                    <div
                                        v-if="attachmentsFor(message).length"
                                        class="assistant-message__attachments"
                                    >
                                        <button
                                            v-for="attachment in downloadableAttachmentsFor(message)"
                                            :key="attachmentKey(attachment)"
                                            type="button"
                                            class="assistant-attachment"
                                            @click="downloadAttachment(attachment)"
                                        >
                                            <v-icon small color="primary">mdi-paperclip</v-icon>
                                            <span class="assistant-attachment__name">{{ attachmentName(attachment) }}</span>
                                            <span class="assistant-attachment__size">{{ formatFileSize(attachment.size) }}</span>
                                        </button>
                                        <span
                                            v-for="attachment in pendingAttachmentsFor(message)"
                                            :key="attachmentKey(attachment)"
                                            class="assistant-attachment assistant-attachment--pending"
                                        >
                                            <v-icon small color="grey darken-1">mdi-paperclip</v-icon>
                                            <span class="assistant-attachment__name">{{ attachmentName(attachment) }}</span>
                                            <span class="assistant-attachment__size">{{ formatFileSize(attachment.size) }}</span>
                                        </span>
                                    </div>
                                    <div
                                        v-if="message.role === 'assistant' && webSourcesFor(message).length"
                                        class="assistant-message__sources"
                                    >
                                        <div class="assistant-message__sources-title">
                                            <v-icon small color="primary">mdi-link-variant</v-icon>
                                            Surse web
                                        </div>
                                        <a
                                            v-for="(source, sourceIndex) in webSourcesFor(message)"
                                            :key="source.url"
                                            :href="source.url"
                                            class="assistant-message__source"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {{ sourceLabel(source, sourceIndex) }}
                                        </a>
                                    </div>
                                    <div class="assistant-message__time">
                                        {{ formatDateTime(message.created_at) }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="loading" class="assistant-message assistant-message--assistant">
                                <div class="assistant-message__content">
                                    <div class="assistant-message__body assistant-message__body--loading">
                                        <v-progress-circular indeterminate size="16" width="2" color="primary" class="assistant-loading-icon" />
                                        Se incarca...
                                    </div>
                                </div>
                            </div>
                        </div>

                        <v-alert v-if="error" dense outlined type="error" class="assistant-error">
                            {{ error }}
                        </v-alert>

                        <div class="assistant-composer-wrap">
                            <div v-if="selectedFiles.length" class="assistant-selected-files">
                                <v-chip
                                    v-for="(file, fileIndex) in selectedFiles"
                                    :key="selectedFileKey(file, fileIndex)"
                                    small
                                    outlined
                                    close
                                    class="assistant-selected-file"
                                    :disabled="loading"
                                    @click:close="removeSelectedFile(fileIndex)"
                                >
                                    <v-icon left small>mdi-paperclip</v-icon>
                                    <span class="assistant-selected-file__name">{{ file.name }}</span>
                                    <span class="assistant-selected-file__size">{{ formatFileSize(file.size) }}</span>
                                </v-chip>
                            </div>

                            <div class="assistant-composer">
                                <input
                                    ref="fileInput"
                                    class="assistant-file-input"
                                    type="file"
                                    multiple
                                    :accept="acceptedAttachmentTypes"
                                    :disabled="loading"
                                    @change="selectFiles"
                                >
                                <v-btn
                                    icon
                                    small
                                    color="grey darken-2"
                                    class="assistant-attach"
                                    :disabled="loading"
                                    title="Ataseaza fisiere"
                                    @click="openFilePicker"
                                >
                                    <v-icon>mdi-paperclip</v-icon>
                                </v-btn>
                                <v-textarea
                                    v-model="draft"
                                    auto-grow
                                    rows="1"
                                    hide-details
                                    solo
                                    flat
                                    dense
                                    :disabled="loading"
                                    placeholder="Scrie o intrebare"
                                    @keydown.enter.exact.prevent="sendMessage"
                                />
                                <v-btn
                                    color="primary"
                                    class="assistant-send"
                                    :loading="loading"
                                    :disabled="!canSend"
                                    fab
                                    small
                                    depressed
                                    title="Trimite"
                                    @click="sendMessage"
                                >
                                    <v-icon>mdi-send</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </v-sheet>
                </section>

                <aside class="assistant-rail">
                    <v-sheet class="assistant-panel assistant-panel--records" outlined>
                        <div class="assistant-panel__header">
                            <div>
                                <div class="assistant-kicker">Rezultate</div>
                                <h2 class="assistant-title">Surse CRM</h2>
                            </div>
                            <v-chip small color="primary" outlined>{{ records.length }}</v-chip>
                        </div>

                        <div class="assistant-records">
                            <v-card
                                v-for="record in records"
                                :key="`${record.type}-${record.id}`"
                                outlined
                                class="assistant-record"
                            >
                                <v-card-text>
                                    <div class="assistant-record__top">
                                        <v-chip x-small :color="recordColor(record.type)" dark>
                                            {{ recordType(record.type) }}
                                        </v-chip>
                                        <span v-if="record.amount_with_vat" class="assistant-record__amount">
                                            {{ record.amount_with_vat | price }}
                                        </span>
                                    </div>
                                    <h3 class="assistant-record__title">{{ record.title }}</h3>
                                    <p class="assistant-record__meta">
                                        {{ record.customer || record.supplier || record.city || '-' }}
                                    </p>
                                    <p class="assistant-record__meta">
                                        {{ record.status || record.payment_type || record.date || '-' }}
                                    </p>
                                    <v-btn
                                        v-if="canOpenRecord(record)"
                                        small
                                        outlined
                                        color="primary"
                                        @click="openRecord(record)"
                                    >
                                        <v-icon left small>mdi-open-in-new</v-icon>
                                        Deschide
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                            <div v-if="records.length === 0" class="assistant-empty assistant-empty--panel">
                                <v-icon color="grey lighten-1" size="34">mdi-database-search-outline</v-icon>
                                Nu exista surse pentru conversatia curenta.
                            </div>
                        </div>
                    </v-sheet>

                    <v-sheet class="assistant-panel assistant-panel--actions" outlined>
                        <div class="assistant-panel__header">
                            <div>
                                <div class="assistant-kicker">Confirmari</div>
                                <h2 class="assistant-title">Actiuni</h2>
                            </div>
                        </div>

                        <v-list dense class="pa-0 assistant-actions">
                            <v-list-item v-for="action in actions" :key="action.id">
                                <v-list-item-content>
                                    <v-list-item-title>{{ action.label || action.action }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ action.status }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="action.status === 'planned'">
                                    <div class="assistant-action-buttons">
                                        <v-btn icon small color="success" @click="confirmAction(action)">
                                            <v-icon>mdi-check</v-icon>
                                        </v-btn>
                                        <v-btn icon small color="error" @click="cancelAction(action)">
                                            <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>

                        <div v-if="actions.length === 0" class="assistant-empty assistant-empty--compact">
                            <v-icon color="grey lighten-1" size="28">mdi-check-circle-outline</v-icon>
                            <div>Nu exista actiuni planificate.</div>
                        </div>
                    </v-sheet>
                </aside>
            </div>
    </v-container>
</template>

<script>
import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import { createNamespacedHelpers } from "vuex";

const workstationHelpers = createNamespacedHelpers("workstation");
const markdown = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: true
});

const defaultLinkOpenRenderer = markdown.renderer.rules.link_open || function(tokens, index, options, env, self) {
    return self.renderToken(tokens, index, options);
};

markdown.renderer.rules.link_open = function(tokens, index, options, env, self) {
    const token = tokens[index];
    token.attrSet("target", "_blank");
    token.attrSet("rel", "noopener noreferrer");

    return defaultLinkOpenRenderer(tokens, index, options, env, self);
};

export default {
    data() {
        return {
            threads: [],
            activeThread: null,
            messages: [],
            records: [],
            actions: [],
            suggestions: [],
            activeSuggestion: null,
            draft: "",
            selectedFiles: [],
            maxSelectedFiles: 5,
            acceptedAttachmentTypes: ".pdf,.txt,.csv,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx",
            loading: false,
            threadsLoading: false,
            pendingRunId: null,
            error: null
        };
    },
    computed: {
        ...workstationHelpers.mapGetters({
            getWorkstation: "getWorkstation",
            getWorkstationId: "getWorkstationId"
        }),
        workstationId() {
            return this.getWorkstationId || 1;
        },
        workstationLabel() {
            return this.getWorkstation ? this.getWorkstation.name : "Punct de lucru";
        },
        canSend() {
            return this.draft.trim().length > 0 && !this.loading;
        }
    },
    async mounted() {
        await Promise.all([
            this.loadSuggestions(),
            this.loadThreads()
        ]);
    },
    beforeDestroy() {
        this.pendingRunId = null;
    },
    methods: {
        endpoint(name, params = {}) {
            return route(name, Object.assign({ workstation: this.workstationId }, params));
        },
        async loadSuggestions() {
            const { data } = await axios.get(this.endpoint("assistant.suggestions.index"));
            this.suggestions = data.data || [];
        },
        async loadThreads() {
            this.threadsLoading = true;
            try {
                const { data } = await axios.get(this.endpoint("assistant.threads.index"));
                this.threads = data.data || [];
                if (!this.activeThread && this.threads.length) {
                    await this.selectThread(this.threads[0]);
                }
            } finally {
                this.threadsLoading = false;
            }
        },
        async selectThread(thread) {
            this.pendingRunId = null;
            this.loading = false;
            this.activeThread = thread;
            const { data } = await axios.get(this.endpoint("assistant.threads.show", { assistantThread: thread.id }));
            const payload = data.data || {};
            this.applyThreadPayload(payload);
        },
        startThread() {
            this.pendingRunId = null;
            this.loading = false;
            this.activeThread = null;
            this.messages = [];
            this.records = [];
            this.actions = [];
            this.error = null;
            this.draft = "";
            this.clearSelectedFiles();
        },
        async archiveThread() {
            if (!this.activeThread) {
                return;
            }
            const { data } = await axios.patch(
                this.endpoint("assistant.threads.update", { assistantThread: this.activeThread.id }),
                { status: "archived" }
            );
            this.activeThread = data.data;
            await this.loadThreads();
            this.startThread();
        },
        useSuggestion(suggestion) {
            this.activeSuggestion = suggestion;
            this.draft = this.suggestionMessage(suggestion);
            this.sendMessage();
        },
        async sendMessage() {
            if (!this.canSend) {
                return;
            }

            const message = this.draft.trim();
            const suggestion = this.activeSuggestion;
            const attachments = this.selectedFiles.slice();
            this.draft = "";
            this.activeSuggestion = null;
            this.clearSelectedFiles();
            this.error = null;
            this.loading = true;
            this.messages.push({
                local_id: `local-${Date.now()}`,
                role: "user",
                content: message,
                metadata: {
                    attachments: attachments.map((file, index) => this.localAttachmentPayload(file, index))
                },
                created_at: new Date().toISOString()
            });
            this.$nextTick(this.scrollToBottom);

            try {
                const payload = this.chatPayload({
                    thread_id: this.activeThread ? this.activeThread.id : null,
                    message,
                    limit: this.suggestionLimit(suggestion),
                    attachments
                });

                const types = this.suggestionTypes(suggestion);

                if (types.length) {
                    types.forEach((type, index) => payload.append(`types[${index}]`, type));
                }

                const { data } = await axios.post(this.endpoint("assistant.chat.store"), payload);

                this.activeThread = data.thread;
                this.pendingRunId = data.run_id;
                await this.waitForRun(data.thread.id, data.run_id);
                await this.loadThreads();
            } catch (e) {
                this.draft = message;
                this.selectedFiles = attachments;
                this.error = this._.get(e, "response.data.message", e.message || "Nu am putut trimite mesajul.");
            } finally {
                this.loading = false;
                this.pendingRunId = null;
                this.$nextTick(this.scrollToBottom);
            }
        },
        chatPayload({ thread_id, message, limit, attachments }) {
            const payload = new FormData();

            if (thread_id) {
                payload.append("thread_id", thread_id);
            }

            payload.append("message", message);
            payload.append("limit", limit);

            attachments.forEach((file, index) => {
                payload.append(`attachments[${index}]`, file);
            });

            return payload;
        },
        openFilePicker() {
            if (this.$refs.fileInput && !this.loading) {
                this.$refs.fileInput.click();
            }
        },
        selectFiles(event) {
            const files = Array.from(event.target.files || []);
            const remainingSlots = this.maxSelectedFiles - this.selectedFiles.length;

            if (remainingSlots <= 0) {
                this.error = `Poti atasa maximum ${this.maxSelectedFiles} fisiere.`;
                event.target.value = "";
                return;
            }

            this.selectedFiles = this.selectedFiles
                .concat(files.slice(0, remainingSlots));
            this.error = null;

            if (files.length > remainingSlots) {
                this.error = `Poti atasa maximum ${this.maxSelectedFiles} fisiere.`;
            }

            event.target.value = "";
        },
        removeSelectedFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        clearSelectedFiles() {
            this.selectedFiles = [];

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        localAttachmentPayload(file, index) {
            return {
                id: `local-${Date.now()}-${index}`,
                file_name: file.name,
                mime_type: file.type,
                size: file.size
            };
        },
        suggestionKey(suggestion) {
            return this._.get(suggestion, "label", suggestion);
        },
        suggestionLabel(suggestion) {
            return this._.get(suggestion, "label", suggestion);
        },
        suggestionMessage(suggestion) {
            return this._.get(suggestion, "message", suggestion);
        },
        suggestionTypes(suggestion) {
            const types = this._.get(suggestion, "types", []);

            return Array.isArray(types) ? types : [];
        },
        suggestionLimit(suggestion) {
            return this._.get(suggestion, "limit", 6);
        },
        async waitForRun(threadId, runId) {
            const attempts = 90;
            const interval = 2000;

            for (let attempt = 0; attempt < attempts; attempt += 1) {
                if (this.pendingRunId !== runId) {
                    return;
                }

                const payload = await this.fetchThread(threadId);
                const run = (payload.runs || []).find(item => item.id === runId);

                if (run && run.status === "completed") {
                    this.applyThreadPayload(payload);
                    return;
                }

                if (run && run.status === "failed") {
                    this.applyThreadPayload(payload);
                    throw new Error(run.error || "Asistentul nu a putut procesa cererea.");
                }

                await this.sleep(interval);
            }

            throw new Error("Raspunsul asistentului dureaza prea mult. Verifica worker-ul de coada.");
        },
        async fetchThread(threadId) {
            const { data } = await axios.get(this.endpoint("assistant.threads.show", { assistantThread: threadId }));

            return data.data || {};
        },
        applyThreadPayload(payload) {
            this.activeThread = payload;
            this.messages = payload.messages || [];
            this.actions = payload.actions || [];
            const lastAssistant = this._.findLast(this.messages, { role: "assistant" });
            this.records = this._.get(lastAssistant, "metadata.records", []);
            this.$nextTick(this.scrollToBottom);
        },
        sleep(ms) {
            return new Promise(resolve => window.setTimeout(resolve, ms));
        },
        async confirmAction(action) {
            const { data } = await axios.patch(
                this.endpoint("assistant.actions.update", { assistantAction: action.id }),
                { status: "confirmed" }
            );
            this.replaceAction(data.data);
        },
        async cancelAction(action) {
            const { data } = await axios.patch(
                this.endpoint("assistant.actions.update", { assistantAction: action.id }),
                { status: "cancelled" }
            );
            this.replaceAction(data.data);
        },
        replaceAction(action) {
            const index = this.actions.findIndex(item => item.id === action.id);
            if (index >= 0) {
                this.$set(this.actions, index, action);
            }
        },
        canOpenRecord(record) {
            return ["offer", "construction_estimate", "invoice", "customer", "expense", "internal_aquisition"].indexOf(record.type) >= 0;
        },
        openRecord(record) {
            const routeName = {
                offer: "offers-edit",
                construction_estimate: "construction-estimates-edit",
                customer: "customers-edit",
                expense: "vehicle-expenses-edit",
                internal_aquisition: "internal-aquisitions-edit"
            }[record.type];

            if (routeName) {
                this.$router.push({ name: routeName, params: { id: record.id } });
            }
        },
        recordType(type) {
            return {
                offer: "Oferta",
                construction_estimate: "Deviz",
                invoice: "Factura",
                customer: "Client",
                expense: "Cheltuiala",
                internal_aquisition: "Achizitie"
            }[type] || type;
        },
        recordColor(type) {
            return {
                offer: "primary",
                construction_estimate: "teal",
                invoice: "indigo",
                customer: "deep-orange",
                expense: "brown",
                internal_aquisition: "blue-grey"
            }[type] || "grey";
        },
        scrollToBottom() {
            if (this.$refs.messages) {
                this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
            }
        },
        formatDateTime(value) {
            if (!value) {
                return "-";
            }

            return window.moment(value).format("DD.MM.YYYY HH:mm");
        },
        webSourcesFor(message) {
            return this._.get(message, "metadata.web_sources", [])
                .filter(source => source && source.url);
        },
        attachmentsFor(message) {
            return this._.get(message, "metadata.attachments", [])
                .filter(attachment => attachment && this.attachmentName(attachment));
        },
        downloadableAttachmentsFor(message) {
            return this.attachmentsFor(message)
                .filter(attachment => attachment.download_url);
        },
        pendingAttachmentsFor(message) {
            return this.attachmentsFor(message)
                .filter(attachment => !attachment.download_url);
        },
        async downloadAttachment(attachment) {
            const { data, headers } = await axios.get(attachment.download_url, {
                responseType: "blob"
            });
            const filename = this.downloadFilename(headers, this.attachmentName(attachment));
            const url = window.URL.createObjectURL(new Blob([data], {
                type: attachment.mime_type || data.type || "application/octet-stream"
            }));
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        downloadFilename(headers, fallback) {
            const contentDisposition = headers["content-disposition"] || "";
            const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

            if (utf8Match) {
                return decodeURIComponent(utf8Match[1]);
            }

            if (asciiMatch) {
                return asciiMatch[1];
            }

            return fallback || "attachment";
        },
        attachmentKey(attachment) {
            return attachment.id || `${attachment.file_name}-${attachment.size}`;
        },
        attachmentName(attachment) {
            return attachment.file_name || attachment.name || "";
        },
        selectedFileKey(file, index) {
            return `${file.name}-${file.size}-${file.lastModified || index}`;
        },
        formatFileSize(size) {
            const bytes = Number(size);

            if (!bytes) {
                return "";
            }

            if (bytes < 1024) {
                return `${bytes} B`;
            }

            if (bytes < 1024 * 1024) {
                return `${Math.round(bytes / 1024)} KB`;
            }

            return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
        },
        sourceLabel(source, index) {
            return `${index + 1}. ${source.url}`;
        },
        formatMessage(value) {
            return DOMPurify.sanitize(markdown.render(value || ""), {
                ADD_ATTR: ["target", "rel"]
            });
        }
    }
};
</script>

<style lang="scss">
.assistant-dashboard {
    background: #f3f5f7;
    min-height: calc(100vh - 64px);
    padding: 12px;
}

.assistant-layout {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(220px, 280px) minmax(420px, 1fr) minmax(260px, 320px);
    height: calc(100vh - 88px);
}

.assistant-column,
.assistant-rail {
    min-height: 0;
}

.assistant-rail {
    display: grid;
    gap: 12px;
    grid-template-rows: minmax(0, 1fr) 210px;
}

.assistant-panel {
    background: #ffffff;
    border-color: #d7dde6 !important;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.assistant-panel--threads,
.assistant-panel--chat {
    min-height: 0;
}

.assistant-panel--records {
    min-height: 0;
}

.assistant-panel--actions {
    min-height: 0;
}

.assistant-panel__header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    min-height: 38px;
    padding: 12px 12px 10px;
}

.assistant-kicker {
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.assistant-title {
    color: #111827;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
}

.assistant-thread-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 8px 8px !important;
}

.assistant-thread {
    border-radius: 5px;
    margin-bottom: 3px;
}

.assistant-thread--active {
    background: #eef5ff;
    box-shadow: inset 3px 0 0 #1976d2;
}

.assistant-chat {
    display: flex;
    flex-direction: column;
}

.assistant-suggestions {
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    gap: 8px;
    margin-bottom: 0;
    overflow-x: auto;
    padding: 0 12px 10px;
    white-space: nowrap;
}

.assistant-suggestion {
    border-color: #d8dee8 !important;
    color: #334155 !important;
    flex: 0 0 auto;
    margin-bottom: 0 !important;
}

.assistant-suggestion:hover {
    background: #f3f7fc;
    border-color: #9bbce4 !important;
}

.assistant-messages {
    background: #fbfcfd;
    border-bottom: 1px solid #e5e7eb;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 14px 12px;
}

.assistant-chat-empty {
    align-items: center;
    color: #6b7280;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    padding: 24px;
    text-align: center;
}

.assistant-chat-empty h3 {
    color: #1f2937;
    font-size: 17px;
    font-weight: 700;
    margin: 12px 0 4px;
}

.assistant-chat-empty p {
    font-size: 13px;
    margin: 0;
}

.assistant-message {
    display: block;
    margin-bottom: 10px;
    max-width: 76%;
    width: fit-content;
}

.assistant-message--user {
    margin-left: auto;
}

.assistant-message--assistant {
    margin-right: auto;
}

.assistant-message__content {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.assistant-message__body {
    background: #ffffff;
    border: 1px solid #dfe6ef;
    border-radius: 7px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    color: #111827;
    display: inline-block;
    font-size: 14px;
    line-height: 1.42;
    min-height: 0;
    padding: 7px 10px;
    word-break: break-word;
}

.assistant-message__body p {
    margin: 0 0 8px;
}

.assistant-message__body p:last-child {
    margin-bottom: 0;
}

.assistant-message__body h1,
.assistant-message__body h2,
.assistant-message__body h3,
.assistant-message__body h4 {
    color: #111827;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    margin: 10px 0 6px;
}

.assistant-message__body h1:first-child,
.assistant-message__body h2:first-child,
.assistant-message__body h3:first-child,
.assistant-message__body h4:first-child {
    margin-top: 0;
}

.assistant-message__body ul {
    margin: 6px 0 8px;
    padding-left: 18px;
}

.assistant-message__body ul:last-child {
    margin-bottom: 0;
}

.assistant-message__body li {
    margin: 2px 0;
}

.assistant-message__body a {
    color: #0f5ea8;
    text-decoration: underline;
}

.assistant-message__body code {
    background: #eef2f7;
    border: 1px solid #dce4ee;
    border-radius: 4px;
    color: #1f2937;
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    padding: 1px 4px;
}

.assistant-message__body table {
    border-collapse: collapse;
    display: block;
    font-size: 12px;
    line-height: 1.35;
    margin: 8px 0;
    max-width: 100%;
    overflow-x: auto;
    white-space: normal;
}

.assistant-message__body th,
.assistant-message__body td {
    border: 1px solid #d9e1ec;
    min-width: 120px;
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
}

.assistant-message__body th {
    background: #f1f5f9;
    color: #334155;
    font-weight: 700;
}

.assistant-message__body tbody tr:nth-child(even) td {
    background: #f8fafc;
}

.assistant-message__sources {
    background: #ffffff;
    border: 1px solid #dfe6ef;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 5px;
    padding: 6px 8px;
}

.assistant-message__sources-title {
    align-items: center;
    color: #334155;
    display: flex;
    font-size: 11px;
    font-weight: 700;
    gap: 4px;
    text-transform: uppercase;
}

.assistant-message__source {
    color: #0f5ea8;
    font-size: 12px;
    line-height: 1.35;
    overflow-wrap: anywhere;
    text-decoration: underline;
}

.assistant-message__attachments {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-top: 5px;
}

.assistant-attachment {
    align-items: center;
    background: #ffffff;
    border: 1px solid #dfe6ef;
    border-radius: 6px;
    color: #1f2937;
    cursor: pointer;
    display: flex;
    font-size: 12px;
    gap: 5px;
    line-height: 1.3;
    max-width: 100%;
    padding: 5px 7px;
    text-decoration: none;
    text-align: left;
    width: 100%;
}

.assistant-attachment:hover {
    background: #f3f7fc;
    border-color: #bfd8fb;
    text-decoration: none;
}

.assistant-attachment--pending {
    color: #4b5563;
}

.assistant-attachment__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.assistant-attachment__size {
    color: #7a8797;
    flex: 0 0 auto;
    font-size: 11px;
}

.assistant-message--user .assistant-message__body {
    background: #edf5ff;
    border-color: #bfd8fb;
    color: #102a43;
}

.assistant-message__body--loading {
    align-items: center;
    display: flex;
}

.assistant-loading-icon {
    margin-right: 8px;
}

.assistant-message__time {
    color: #8b98a8;
    font-size: 10px;
    line-height: 1;
    margin-top: 4px;
}

.assistant-message--user .assistant-message__time {
    text-align: right;
}

.assistant-composer-wrap {
    background: #ffffff;
    padding: 10px 12px 12px;
}

.assistant-selected-files {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 7px;
}

.assistant-selected-file {
    border-color: #d8dee8 !important;
    max-width: 100%;
}

.assistant-selected-file__name {
    display: inline-block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
}

.assistant-selected-file__size {
    color: #7a8797;
    font-size: 11px;
    margin-left: 5px;
}

.assistant-composer {
    align-items: center;
    background: #ffffff;
    border: 1px solid #dfe6ef;
    border-radius: 7px;
    display: flex;
    margin: 0;
    padding: 4px 5px 4px 8px;
}

.assistant-file-input {
    display: none;
}

.assistant-attach {
    flex: 0 0 auto;
    margin-right: 4px;
}

.assistant-composer .v-input__slot {
    box-shadow: none !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
}

.assistant-composer textarea {
    line-height: 1.45;
    max-height: 112px;
    padding-top: 7px !important;
}

.assistant-send {
    flex: 0 0 auto;
    margin-left: 6px;
}

.assistant-error {
    margin: 10px 12px 0;
}

.assistant-records {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 8px 8px;
}

.assistant-record {
    border-color: #e1e6ee !important;
    border-radius: 6px;
    margin-bottom: 8px;
}

.assistant-record .v-card__text {
    padding: 11px 12px;
}

.assistant-record__top {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 7px;
}

.assistant-record__amount {
    color: #111827;
    font-size: 12px;
    font-weight: 700;
}

.assistant-record__title {
    color: #111827;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 6px;
    word-break: break-word;
}

.assistant-record__meta {
    color: #64748b;
    font-size: 13px;
    line-height: 1.35;
    margin-bottom: 4px;
}

.assistant-empty {
    align-items: center;
    color: #64748b;
    display: flex;
    font-size: 13px;
    gap: 8px;
    line-height: 1.35;
}

.assistant-empty--panel {
    flex-direction: column;
    justify-content: center;
    min-height: 150px;
    padding: 16px;
    text-align: center;
}

.assistant-empty--compact {
    justify-content: center;
    min-height: 74px;
    padding: 10px;
}

.assistant-action-buttons {
    display: flex;
}

.assistant-actions {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 8px !important;
}

@media (max-width: 960px) {
    .assistant-layout {
        display: flex;
        flex-direction: column;
        height: auto;
    }

    .assistant-panel {
        min-height: 260px;
    }

    .assistant-panel--chat {
        min-height: 560px;
    }

    .assistant-rail {
        display: flex;
        flex-direction: column;
    }

    .assistant-composer {
        align-items: center;
        flex-direction: row;
    }

    .assistant-selected-file__name {
        max-width: 160px;
    }

    .assistant-message {
        max-width: 90%;
    }
}
</style>
